/**
 * EditPreferencesDialog — change a registration's performance
 * preferences after the row was created.
 *
 * Constraints (mirrored on the backend):
 *   - Only the row owner can edit (no buddy-group-managed edits).
 *   - Buddy-group rows are LOCKED — the dashboard / event-detail
 *     surfaces hide the affordance entirely on those rows. This
 *     dialog assumes the caller already gated on `buddy_group_id`.
 *   - Pair must validate against the rodeo's `preference_config`.
 *   - Entry-close cutoff is enforced server-side; on 409 the dialog
 *     surfaces the API message verbatim.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import {
  PerformancePreferenceStep,
  type PerformanceChoice,
} from './PerformancePreferenceStep';
import { httpClient } from '@/api/http-client';
import { useRegistrationPolicy } from '@/api/extensions/useRegistrationOps';
import { useUpdateRegistrationPreferences } from '@/api/extensions/useRegistrationOps';
import { useToast } from '@/contexts/ToastContext';
import { formatInTimezone } from '@/utils/timezone';

interface EditPreferencesDialogProps {
  open: boolean;
  onClose: () => void;
  registrationId: string;
  eventId: string;
  rodeoId: string | null | undefined;
  /** Current selections — used to pre-fill the form */
  currentFirst: string | null;
  currentSecond: string | null;
  /** Optional event display label for the modal title */
  eventName?: string;
}

export function EditPreferencesDialog({
  open,
  onClose,
  registrationId,
  eventId,
  rodeoId,
  currentFirst,
  currentSecond,
  eventName,
}: EditPreferencesDialogProps) {
  const { showSuccess, showError } = useToast();
  const updatePrefs = useUpdateRegistrationPreferences();
  const { data: policy } = useRegistrationPolicy(eventId);

  const [first, setFirst] = useState<string>(currentFirst ?? '');
  const [second, setSecond] = useState<string>(currentSecond ?? '');
  const [error, setError] = useState<string | null>(null);
  const [perfOptions, setPerfOptions] = useState<PerformanceChoice[] | null>(
    null,
  );

  // Reset local form state every time the dialog re-opens so a stale
  // typed value from a prior session doesn't leak into the next edit.
  useEffect(() => {
    if (open) {
      setFirst(currentFirst ?? '');
      setSecond(currentSecond ?? '');
      setError(null);
    }
  }, [open, currentFirst, currentSecond]);

  // The preferred display timezone for the dropdown labels. Empty
  // string falls through to the viewer's local zone in
  // `formatInTimezone` (it requires a string parameter, not undefined).
  // Matches the RegistrationModal's pattern so users see the same time
  // strings here they saw at registration.
  const perfDropdownTz: string = policy?.timezone || '';

  // Fetch performances for the rodeo. Mirrors the build-up logic in
  // RegistrationModal so the option values + labels round-trip identically
  // (the stored `first_performance_preference` may have a `[isoDatetime]`
  // disambiguation suffix appended for same-named performances).
  useEffect(() => {
    if (!open || !rodeoId) {
      setPerfOptions(null);
      return;
    }
    let cancelled = false;
    httpClient
      .get<{
        data: Array<{
          id: string;
          performance_name: string | null;
          is_slack: boolean | null;
          start_datetime: string | null;
        }>;
      }>(`/performances?trig_rodeo_id=${rodeoId}&limit=50`)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data ?? [];
        const seen = new Set<string>();
        const opts: PerformanceChoice[] = [...rows]
          .filter((p) => !!p.performance_name)
          .sort((a, b) => {
            const aTime = a.start_datetime ? Date.parse(a.start_datetime) : 0;
            const bTime = b.start_datetime ? Date.parse(b.start_datetime) : 0;
            return aTime - bTime;
          })
          .reduce<PerformanceChoice[]>((acc, p) => {
            const name = p.performance_name as string;
            const isSlack = !!p.is_slack;
            const isoDate = p.start_datetime ?? '';
            const dedupeKey = `${isSlack ? 'slack' : 'perf'}::${name}::${isoDate}`;
            if (seen.has(dedupeKey)) return acc;
            seen.add(dedupeKey);
            const baseValue = isSlack ? 'Slack' : name;
            const value = isoDate ? `${baseValue} [${isoDate}]` : baseValue;
            const dateLabel = isoDate
              ? formatInTimezone(isoDate, perfDropdownTz, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '';
            const labelName =
              isSlack && name.toLowerCase() === 'slack' ? 'Slack' : name;
            const label = dateLabel ? `${labelName} — ${dateLabel}` : labelName;
            acc.push({ value, label, isSlack });
            return acc;
          }, []);
        setPerfOptions(opts.length > 0 ? opts : null);
      })
      .catch(() => {
        if (!cancelled) setPerfOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, rodeoId, perfDropdownTz]);

  const isDirty = useMemo(() => {
    return (first || '') !== (currentFirst ?? '') || (second || '') !== (currentSecond ?? '');
  }, [first, second, currentFirst, currentSecond]);

  const submitting = updatePrefs.isPending;

  const handleSubmit = async () => {
    setError(null);
    if (!first.trim()) {
      setError('Pick a 1st preference.');
      return;
    }
    try {
      await updatePrefs.mutateAsync({
        registrationId,
        first_performance_preference: first || null,
        second_performance_preference: second || null,
      });
      showSuccess('Preferences updated');
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to update preferences';
      setError(msg);
      showError('Could not update preferences', msg);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit performance preferences</DialogTitle>
          <DialogDescription>
            {eventName
              ? `Update your preferences for ${eventName}. Performance preferences apply across the rodeo, so changing them here lines this entry up with your other entries at the same rodeo.`
              : 'Update your performance preferences. They apply across the rodeo.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <PerformancePreferenceStep
            performanceOptions={perfOptions ?? undefined}
            // Fallback to a generic 3-slot list when the performances
            // endpoint hasn't yet returned (or returned nothing). The
            // stored value still round-trips because PerformancePreferenceStep
            // matches on the raw value.
            performances={
              !perfOptions
                ? ['Performance 1', 'Performance 2', 'Performance 3']
                : undefined
            }
            firstPreference={first}
            secondPreference={second}
            onFirstChange={setFirst}
            onSecondChange={setSecond}
            preferenceConfig={policy?.preference_config ?? null}
            error={error ?? undefined}
            // Anchor preference datetime labels to the rodeo's saved
            // zone with abbreviation appended ("Fri, May 15, 6:00 PM
            // PT") so they match what the host sees, not the viewer's
            // local clock.
            timezone={perfDropdownTz || null}
          />

          {error && (
            <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !isDirty}>
            {submitting ? 'Saving…' : 'Save preferences'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
